<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ChallengeConfiguration, ChallengeProgressEntry } from '@/types/v2/task/challenge/configuration.types';
import { useRouter } from 'vue-router';
import { useChallenge } from "@/composables/tasks/challenge/useChallenge";
import { useChallengeConfigurations } from "@/composables/tasks/challenge/useChallengeConfigurations";
import { useRecord } from "@/composables/tasks/challenge/useChallengeRecords";
import { useNotifications } from "@/composables/useNotifications";

import FooterActions from '@/components/v2/Tasks/Challenge/Buttons/ActionButtons/FooterActions.vue';
import ChallengeRevisionModal from "@/components/v2/Tasks/Challenge/Modals/ChallengeRevisionModal.vue";
import { useWeight } from "@/composables/tasks/challenge/useChallengeWeight";
import { useTask } from "@/composables/tasks/useTask";
import { TasksService } from "@/services/v2/tasks";
import { useEquipment } from "@/composables/tasks/useEquipment";
import TaskCompletionBlockedModal from "@/components/v2/Tasks/Task/TaskCompletionBlockedModal.vue";

// ===================================================================
// Composables
// ===================================================================
const router = useRouter();
const { activeTask } = useTask();
const { configurations } = useChallenge();
const { getConfigurationTags } = useChallengeConfigurations();
const { getChallengeRecordsByConfigKey } = useRecord();
const { getAverageWeightByConfigKeyChallengeRecords } = useWeight();
const { showTaskCompletionSuccess, showError } = useNotifications();
const { getWeightIdCompletionValidation } = useEquipment();

// ===================================================================
// State
// ===================================================================
const activeConfigKey = ref<string>('');
const challengeRevisionModal = ref<boolean>(false);
const selectedEntry = ref<ChallengeProgressEntry | null>(null);
const selectedEntryIndex = ref<number>(-1);
const isFinishing = ref<boolean>(false);
const completionBlockModal = ref<boolean>(false);

// ===================================================================
// Actions
// ===================================================================
// Go back
const goBack = () => router.go(-1);

// Open revisions modal
const openRevisionsModal = (entry: ChallengeProgressEntry, entryIndex: number) => {
    challengeRevisionModal.value = true;
    selectedEntry.value = entry;
    selectedEntryIndex.value = entryIndex;
}

// Finish task handler
const handleFinishTask = async () => {
    if (!activeTask.value) return;

    const validation = getWeightIdCompletionValidation();
    if (!validation.isValid) {
        completionBlockModal.value = true;
        return;
    }
    
    isFinishing.value = true;
    
    try {
        await TasksService.finishTask(activeTask.value);
        showTaskCompletionSuccess(activeTask.value.title);
    } catch (error) {
        console.error('Error completing challenge task:', error);
        showError("Task Completion Failed", "Failed to complete the challenge task. Please try again.");
    } finally {
        isFinishing.value = false;
    }
}

// ===================================================================
// Computed
// ===================================================================
const challengeRecords = computed<ChallengeProgressEntry[] | []>(() => getChallengeRecordsByConfigKey(activeConfigKey.value));
const averageWeight = computed<number | string>(() => getAverageWeightByConfigKeyChallengeRecords(activeConfigKey.value));
const completionValidation = computed(() => getWeightIdCompletionValidation());
const getDestinationTankName = (config: ChallengeConfiguration): string => {
    const destinationTankName = (config as any)?.destinationTank?.name || (config as any)?.destinationTank?.label;
    if (destinationTankName) return destinationTankName;
    return (config as any)?.tank?.name || (config as any)?.tank?.label || 'No destination tank';
};
const getTagCombinationLabel = (config: ChallengeConfiguration): string => {
    const tagTitles = getConfigurationTags(config).map(tag => tag.title);
    return tagTitles.length ? tagTitles.join(' + ') : 'Unmarked';
};

// ===================================================================
// Watchers
// ===================================================================
// Watch for configurations composable state change
watch(
    () => configurations.value,
    (newConfigurations) => {
        if (!activeConfigKey.value && newConfigurations.length > 0) {
            activeConfigKey.value = newConfigurations[0].key;
        }
    },
    { immediate: true }
);
</script>

<template>
    <div class="flex flex-col h-full justify-between">
        <div class="space-y-6">
            <!-- Header -->
            <div class="flex justify-between items-center">
                <h1 class="text-2xl font-bold text-gray-800">Challenge Administration Records</h1>
                <button
                    @click="goBack"
                    class="text-gray-600 hover:text-gray-800 flex items-center gap-1 text-sm"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    Back
                </button>
            </div>
            <span class="w-full bg-secondary h-[1px] block"></span>

            <!-- Stats Cards -->
            <div class="grid grid-cols-3 gap-4">
                <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <h3 class="text-sm font-medium text-gray-500 mb-1">Total Challenged Fish</h3>
                    <p class="text-2xl font-bold text-gray-800">
                        {{ challengeRecords.reduce((sum, record) => sum + (record.challenged || 0), 0) }}
                    </p>
                </div>
                <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <h3 class="text-sm font-medium text-gray-500 mb-1">Average Weight <span class="text-xs">(grams)</span></h3>
                    <p class="text-2xl font-bold text-gray-800">
                        {{ averageWeight }}
                    </p>
                </div>
                <div class="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <h3 class="text-sm font-medium text-gray-500 mb-1">Total Records</h3>
                    <p class="text-2xl font-bold text-gray-800">{{ challengeRecords.length }}</p>
                </div>
            </div>
            <!-- Configuration Tabs -->
            <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <!-- Tab navigation -->
                <div class="flex overflow-x-auto border-b border-gray-200">
                    <template v-for="(config, index) in configurations" :key="config.key">
                        <button
                            @click="activeConfigKey = config.key"
                            class="px-4 py-2 text-sm font-medium whitespace-nowrap flex items-center gap-1"
                            :class="activeConfigKey === config.key
                              ? 'text-primary border-b-2 border-primary'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'"
                        >
                            <div class="flex flex-col items-start gap-1">
                                <span class="text-xs font-semibold">{{ getDestinationTankName(config) }}</span>
                                <span class="text-xs">{{ getTagCombinationLabel(config) }}</span>
                            </div>
                        </button>
                        <div
                            v-if="index < configurations.length - 1"
                            class="h-6 w-px bg-gray-300 self-center"
                        ></div>
                    </template>
                </div>

                <!-- Table -->
                <div class="overflow-x-auto">
                    <div v-if="!challengeRecords.length" class="text-center py-8 text-gray-500">
                        No challenge records for this configuration
                    </div>

                    <table v-else class="min-w-full">
                        <thead class="bg-gray-50">
                        <tr>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                #
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Comment
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Challenged Fish
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Weight
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Challenge Duration
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                No. Revisions
                            </th>
                            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200">
                        <tr v-for="(record, index) in challengeRecords" :key="index" class="hover:bg-gray-50">
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ index + 1 }}
                            </td>
                            <td class="px-6 py-4 text-sm text-gray-500 max-w-xs">
                                {{ record.comment || 'No comment' }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {{ record.challenged || 0 }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ record.weight || 'N/A' }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ record.challengeDuration || 'N/A' }} {{ record.challengeDuration ? 'minutes' : '' }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {{ record.revisions?.length || 0 }}
                            </td>
                            <td>
                                <button
                                    @click="openRevisionsModal(record, index)"
                                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                >
                                    Add Revision
                                </button>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <ChallengeRevisionModal
            :entry="selectedEntry"
            :configKey="activeConfigKey"
            :entryIndex="selectedEntryIndex"
            :isOpen="challengeRevisionModal"
            v-on:close="() => challengeRevisionModal = false"
        />
        <TaskCompletionBlockedModal
            :show="completionBlockModal"
            :reason="completionValidation.reason"
            @close="completionBlockModal = false"
        />

        <!-- Footer -->
        <FooterActions>
            <template #middle>
                <button
                    @click="goBack"
                    class="w-full"
                >
                    Back
                </button>
            </template>
            <template #end>
                <div class="w-full text-center">
                    <button
                        @click="handleFinishTask"
                        :disabled="isFinishing"
                        class="w-full relative"
                        :class="{ 'opacity-50 cursor-not-allowed': isFinishing }"
                    >
                        <span v-if="isFinishing" class="flex items-center justify-center">
                            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Finishing...
                        </span>
                        <span v-else>
                            Finish Task
                        </span>
                    </button>
                </div>
            </template>
        </FooterActions>
    </div>
</template>
